import React, { useCallback, useContext } from 'react';
import type { TitleSettings, TitlePageT } from 'venn-components';
import { StudioPrintSettingsContext, useAppPrintMode, useDebounceToGlobal, UserContext } from 'venn-components';
import { Img } from '../shared';
import moment from 'moment';
import {
  CoverPageBackground,
  CoverPageOrganizationName,
  CoverPageRow,
  InlineInput,
  MultilineInput,
  useUpdatePage,
} from './Shared';
import styled from 'styled-components';
import { validate as uuidValidate } from 'uuid';
import { isNil } from 'lodash';
import { useRecoilValue } from 'recoil';
import {
  DEFAULT_STOCK_PHOTO,
  pageFonts,
  studioTitlePageHideCompanyLogo,
  studioTitlePageHasDisclosureTextAlignedRight,
  studioTitlePageHasFullPageBackgroundImage,
  viewCoverPageImage,
} from 'venn-state';
import {
  STUDIO_COVER_PAGE_DEFAULT_DISCLOSURE,
  STUDIO_COVER_PAGE_DISCLOSURE_DEFAULT_FONT_SIZE,
  useHasFF,
} from 'venn-utils';

const TitlePage = ({ pageNumber, page }: { pageNumber: number; page: TitlePageT }) => {
  const hasCustomizationEnhancements = useHasFF('rl_customization_enhancements_ff');

  const { organizationPrintSettings } = useContext(StudioPrintSettingsContext);
  const { inPrintMode } = useAppPrintMode();
  const { defaultLogoId } = useContext(StudioPrintSettingsContext);
  const { profileSettings } = useContext(UserContext);
  const customCoverPageImage = useRecoilValue(viewCoverPageImage);
  const updatePage = useUpdatePage(pageNumber);
  const coverPageImageId = uuidValidate(customCoverPageImage) ? customCoverPageImage : DEFAULT_STOCK_PHOTO;
  const coverPageImage = `/api/images/${coverPageImageId}`;

  const hasFullPageBackgroundImage = useRecoilValue(studioTitlePageHasFullPageBackgroundImage);
  const hasDisclosureAlignedRight = useRecoilValue(studioTitlePageHasDisclosureTextAlignedRight);
  const hideCompanyLogoSetting = useRecoilValue(studioTitlePageHideCompanyLogo);

  const applyFullPageBackground = hasCustomizationEnhancements && hasFullPageBackgroundImage;
  const isBottomRowAlignedRight = hasCustomizationEnhancements && hasDisclosureAlignedRight;
  const shouldHideCompanyLogo = hasCustomizationEnhancements && hideCompanyLogoSetting;

  const onChangeSettings = useCallback(
    (settings: TitleSettings) => {
      updatePage({
        titleSettings: settings,
      });
    },
    [updatePage],
  );

  const [titleSettings, setTitleSettings] = useDebounceToGlobal(page.titleSettings, onChangeSettings);

  const onTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const title = e.target.value;
      setTitleSettings((cur) => ({
        ...cur,
        title,
      }));
    },
    [setTitleSettings],
  );

  const onSubtitleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const subTitle = e.target.value;
      setTitleSettings((cur) => ({
        ...cur,
        subTitle,
      }));
    },
    [setTitleSettings],
  );

  const onAdditionalTextChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const additionalText = e.target.value;
      setTitleSettings((cur) => ({
        ...cur,
        additionalText,
      }));
    },
    [setTitleSettings],
  );

  const onCaptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const caption = e.target.value;
      setTitleSettings((cur) => ({
        ...cur,
        caption,
      }));
    },
    [setTitleSettings],
  );

  const currentDate = moment(new Date()).format('MMMM Do, YYYY');

  const fontSettings = useFontSettings();

  const companyLogo = !shouldHideCompanyLogo ? (
    defaultLogoId ? (
      <ImgWrapper>
        <Img src={`/api/printing/logo/${defaultLogoId}`} />
      </ImgWrapper>
    ) : (
      <CoverPageOrganizationName>{profileSettings?.organization.name}</CoverPageOrganizationName>
    )
  ) : null;

  return (
    <>
      <CoverPageRow height="25%">
        <CoverPageTopRow>
          <div style={{ flex: 1, textAlign: 'right' }}>
            <InlineInput
              $inPrintMode={inPrintMode}
              placeholder={`<Enter caption, i.e. ${currentDate}>`}
              value={isNil(titleSettings.caption) ? currentDate : titleSettings.caption}
              onChange={onCaptionChange}
              extraWidth={inPrintMode && hasCustomizationEnhancements ? 15 : 0}
              style={{
                textAlign: 'right',
                marginRight: '-8px',
                ...fontSettings.caption,
              }}
              data-testid="qa-title-page-date"
            />
            <MultilineInput
              style={fontSettings.title}
              inPrintMode={inPrintMode}
              placeholder="<Enter Document Title>"
              value={titleSettings.title}
              maxRows={3}
              onChange={onTitleChange}
              data-testid="qa-title-page-title"
            />
            <MultilineInput
              style={{
                ...fontSettings.subTitle,
                fontWeight: 'lighter',
              }}
              inPrintMode={inPrintMode}
              placeholder="<Enter SubTitle>"
              value={titleSettings.subTitle}
              maxRows={3}
              onChange={onSubtitleChange}
              data-testid="qa-title-page-subtitle"
            />
          </div>
        </CoverPageTopRow>
      </CoverPageRow>
      <CoverPageRow height="50%">
        {!applyFullPageBackground && <CoverPageLogo src={coverPageImage} alt="logo" data-testid="logo-image" />}
      </CoverPageRow>
      <CoverPageRow height="25%">
        <div
          style={{
            margin: '40px',
          }}
        >
          <MultilineInput
            style={{
              ...fontSettings.additionalText,
              fontWeight: 'lighter',
            }}
            inPrintMode={inPrintMode}
            placeholder="<Enter Additional Text>"
            value={titleSettings.additionalText}
            maxRows={3}
            onChange={onAdditionalTextChange}
            data-testid="qa-title-page-additional-text"
          />
        </div>
        <BottomRow alignedRight={isBottomRowAlignedRight}>
          <CoverPageDisclosure
            fontSize={
              organizationPrintSettings?.coverPageDisclosureFontSize ?? STUDIO_COVER_PAGE_DISCLOSURE_DEFAULT_FONT_SIZE
            }
            alignedRight={isBottomRowAlignedRight}
            data-testid="qa-title-page-disclosure"
            dangerouslySetInnerHTML={{
              __html: organizationPrintSettings?.coverPageDisclosure ?? STUDIO_COVER_PAGE_DEFAULT_DISCLOSURE,
            }}
          />
          {companyLogo}
        </BottomRow>
      </CoverPageRow>
      {applyFullPageBackground && <CoverPageBackground src={coverPageImage} />}
    </>
  );
};

export default TitlePage;

function useFontSettings() {
  const dateFont = useRecoilValue(pageFonts.titlePageDate);
  const titleFont = useRecoilValue(pageFonts.titlePageTitle);
  const subTitleFont = useRecoilValue(pageFonts.titlePageSubtitle);
  const contentFont = useRecoilValue(pageFonts.titlePageContent);

  return {
    caption: {
      fontSize: `${dateFont.fontSizePt}pt`,
      lineHeight: dateFont.lineHeight,
    },
    title: {
      fontSize: `${titleFont.fontSizePt}pt`,
      lineHeight: titleFont.lineHeight,
    },
    subTitle: {
      fontSize: `${subTitleFont.fontSizePt}pt`,
      lineHeight: subTitleFont.lineHeight,
    },
    additionalText: {
      fontSize: `${contentFont.fontSizePt}pt`,
      lineHeight: contentFont.lineHeight,
    },
  };
}

const CoverPageTopRow = styled.div`
  position: relative;
  top: 50%;
  transform: translateY(-50%);
  justify-content: flex-end;
  display: flex;
  margin: 0 40px;
`;

const CoverPageLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const BottomRow = styled.div<{ alignedRight: boolean }>`
  display: flex;
  justify-content: ${({ alignedRight }) => (alignedRight ? 'flex-end' : 'space-between')};
  flex-direction: column;
  flex-flow: nowrap;
  overflow: hidden;
  margin-left: 40px;
  margin-right: 40px;
  position: absolute;
  bottom: 40px;
  left: 0px;
  right: 0px;
  padding: 0px;
  align-items: flex-end;
`;
const CoverPageDisclosure = styled.div<{ fontSize: number; alignedRight: boolean }>`
  flex: 0 1 50%;
  white-space: pre-wrap;
  text-align: ${({ alignedRight }) => (alignedRight ? 'right' : 'left')};
  font-size: ${({ fontSize }) => fontSize}pt;
  margin-right: ${({ alignedRight }) => (alignedRight ? '0' : '40px')};
  order: ${({ alignedRight }) => (alignedRight ? '10' : '0')};
  margin-left: ${({ alignedRight }) => (alignedRight ? '40px' : '0')};
  & p {
    margin: 0;
  }
`;

export const ImgWrapper = styled.div`
  height: 60px;
  margin: 0;
  padding: 0;
`;
